import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import { useSeo } from "@/hooks/useSeo";
import { cinemaShows, provinces, cityMap } from "@/mocks/cinemas";
import { getMovieById, getGenreLabel, formatDate } from "@/lib/movie";

const PAGE_SIZE = 8;

export default function Cinema() {
  const [province, setProvince] = useState("全部地区");
  const [city, setCity] = useState("全部城市");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: "影院上映动态 - 最新电影",
    description:
      "最新电影影院上映动态页，按地区汇总各大影院的当前上映影片、影厅类型与场次信息，帮助你了解身边影院的最新排片情况（演示数据）。",
    keywords: "影院排片,电影院上映,影院动态,电影场次,最新电影",
    canonicalPath: "/cinema",
  });

  const filtered = useMemo(() => {
    return cinemaShows.filter((s) => {
      if (province !== "全部地区" && s.province !== province) return false;
      if (city !== "全部城市" && s.city !== city) return false;
      return true;
    });
  }, [province, city]);

  const shown = filtered.slice(0, visible);

  const handleProvince = (v: string) => {
    setProvince(v);
    setCity("全部城市");
    setVisible(PAGE_SIZE);
  };

  return (
    <PageLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-10 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-background-400 mb-4">
            <Link to="/" className="hover:text-background-100 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">影院动态</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-heading font-black text-background-50">
            影院上映动态
          </h1>
          <p className="mt-2 text-sm text-background-300 max-w-2xl leading-relaxed">
            汇总各地影院当前正在上映的影片、影厅类型与场次安排。选择所在地区即可查看附近的影院排片情况。
            以下场次与影院信息均为演示数据，实际以影院现场为准。
          </p>
        </div>
      </div>

      <div className="w-full max-w-[1360px] mx-auto px-4 md:px-6 py-8 md:py-10">
        {/* Region filter */}
        <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-map-pin-2-line text-accent-500"></i>
            <span className="text-sm font-semibold text-foreground-900">按地区筛选</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-foreground-600 mb-2">省份 / 直辖市</p>
              <div className="relative">
                <select
                  value={province}
                  onChange={(e) => handleProvince(e.target.value)}
                  className="w-full appearance-none rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 px-3 py-2.5 pr-9 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  {provinces.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground-600 mb-2">城市</p>
              <div className="relative">
                <select
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setVisible(PAGE_SIZE);
                  }}
                  className="w-full appearance-none rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 px-3 py-2.5 pr-9 focus:outline-none focus:border-primary-400 cursor-pointer"
                >
                  {(cityMap[province] ?? ["全部城市"]).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {["北京市", "上海市", "广东省", "四川省", "浙江省", "全部地区"].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handleProvince(p)}
                className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  province === p
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-foreground-600 mt-6 mb-4">
          当前地区共 <strong className="text-accent-600">{filtered.length}</strong> 条影院上映信息
        </p>

        {shown.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-20 flex flex-col items-center text-center">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-store-2-line text-2xl"></i>
            </span>
            <h3 className="mt-4 text-foreground-900 font-semibold">暂无相关影院上映信息</h3>
            <p className="mt-1.5 text-sm text-foreground-500">换个地区试试，或浏览全部地区的影院动态</p>
            <button
              type="button"
              onClick={() => handleProvince("全部地区")}
              className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              查看全部地区
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {shown.map((show) => {
              const movie = getMovieById(show.movieId);
              if (!movie) return null;
              return (
                <div
                  key={show.id}
                  className="flex flex-col sm:flex-row gap-4 rounded-lg bg-background-50 border border-background-200 p-4 hover:border-accent-300 transition-colors"
                >
                  <Link to={`/detail/${movie.id}`} className="shrink-0 cursor-pointer">
                    <div className="w-full sm:w-[92px] h-[138px] rounded-md overflow-hidden bg-background-200">
                      <img
                        src={movie.poster}
                        alt={`${movie.title} 电影海报`}
                        title={`${movie.title} - 影院上映`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900">
                        {getGenreLabel(movie.category)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-accent-500"></i>
                        {show.city}
                      </span>
                    </div>
                    <Link to={`/detail/${movie.id}`} className="cursor-pointer">
                      <h3 className="mt-2 font-semibold text-foreground-950 hover:text-accent-600 transition-colors truncate">
                        {movie.title}
                      </h3>
                    </Link>
                    <p className="text-xs text-foreground-600 mt-1 truncate">
                      <i className="ri-building-2-line mr-1"></i>
                      {show.cinemaName}
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">
                      <i className="ri-movie-2-line mr-1"></i>
                      {show.hallType} · 上映中
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {show.showtimes.map((t) => (
                        <span
                          key={t}
                          className="text-[11px] px-2 py-0.5 rounded bg-primary-50 text-primary-700"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <p className="text-[11px] text-foreground-400 mt-2.5">
                      {formatDate(show.showDate)} · 场次信息仅为演示示例，非实时票务
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {shown.length > 0 && visible < filtered.length && (
          <div className="flex justify-center mt-10">
            <button
              type="button"
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-md border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              加载更多影院动态
              <i className="ri-arrow-down-line"></i>
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
}